import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import DeveloperBoardOutlinedIcon from '@mui/icons-material/DeveloperBoardOutlined';
import {
    Alert,
    Box,
    Chip,
    LinearProgress,
    Paper,
    Stack,
    Typography,
} from '@mui/material';

type MachineStatusPanelProps = {
    baseUrl: string;
    onResponse: (value: unknown) => void;
};

type MetricTileProps = {
    icon: ReactElement;
    label: string;
    value: string;
    helper?: string;
};

const refreshMs = 3_000;

function trimTrailingSlash(value: string): string {
    return value.trim().replace(/\/+$/, '');
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

function asString(value: unknown, fallback = 'N/A'): string {
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return fallback;
}

function asNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function formatPercent(value: unknown): string {
    const number = asNumber(value);
    return number === null ? 'N/A' : `${Math.round(number)}%`;
}

function formatBytes(value: unknown): string {
    const bytes = asNumber(value);
    if (bytes === null || bytes <= 0) return 'N/A';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getNested(record: Record<string, unknown>, keys: string[]): unknown {
    let current: unknown = record;
    for (const key of keys) {
        current = asRecord(current)[key];
    }
    return current;
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const record = asRecord(payload);
    const message = record.detail ?? record.message ?? record.error ?? record.errorMessage;
    return typeof message === 'string' && message.trim() ? message : fallback;
}

async function requestSystemStatus(baseUrl: string): Promise<unknown> {
    const response = await fetch(`${trimTrailingSlash(baseUrl)}/api/system`, {
        headers: { Accept: 'application/json' },
    });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return payload;
}

function MetricTile({ icon, label, value, helper }: MetricTileProps) {
    return (
        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 190px', minWidth: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
                {icon}
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary">
                        {label}
                    </Typography>
                    <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
                        {value}
                    </Typography>
                    {helper ? (
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                            {helper}
                        </Typography>
                    ) : null}
                </Box>
            </Stack>
        </Paper>
    );
}

export function MachineStatusPanel({ baseUrl, onResponse }: MachineStatusPanelProps) {
    const [status, setStatus] = useState<unknown>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

    const statusRecord = asRecord(status);
    const cpuRecord = asRecord(statusRecord.cpu);
    const memoryRecord = asRecord(statusRecord.memory ?? statusRecord.ram);
    const gpus = useMemo(() => {
        const direct = asArray(statusRecord.gpus);
        if (direct.length > 0) return direct;
        const gpu = statusRecord.gpu;
        return Array.isArray(gpu) ? gpu : gpu ? [gpu] : [];
    }, [statusRecord.gpu, statusRecord.gpus]);

    const loadStatus = async () => {
        setLoading(true);
        try {
            const result = await requestSystemStatus(baseUrl);
            setStatus(result);
            setError('');
            setLastUpdatedAt(new Date().toLocaleTimeString());
            onResponse(result);
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Unable to load machine status');
            setLastUpdatedAt(new Date().toLocaleTimeString());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadStatus();
        const intervalId = window.setInterval(() => {
            void loadStatus();
        }, refreshMs);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [baseUrl]);

    const platform = asString(statusRecord.platform ?? statusRecord.system ?? statusRecord.os);
    const cpuName = asString(cpuRecord.name ?? cpuRecord.model ?? statusRecord.cpu_name);
    const cpuUsage = formatPercent(cpuRecord.percent ?? cpuRecord.usage_percent ?? statusRecord.cpu_percent);
    const memoryUsed = formatBytes(memoryRecord.used ?? memoryRecord.used_bytes ?? getNested(statusRecord, ['ram', 'used_bytes']));
    const memoryTotal = formatBytes(memoryRecord.total ?? memoryRecord.total_bytes ?? getNested(statusRecord, ['ram', 'total_bytes']));
    const memoryPercent = formatPercent(memoryRecord.percent ?? memoryRecord.usage_percent ?? statusRecord.memory_percent);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {loading ? <LinearProgress /> : null}
            <Stack spacing={2.5} sx={{ p: 2.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6">Machine Status</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Live API host CPU, memory, and GPU status.
                        </Typography>
                    </Box>
                    <Chip size="small" color={error ? 'warning' : 'success'} label={error ? 'Offline' : 'Live: 3s'} />
                    <Chip size="small" variant="outlined" label={`Updated ${lastUpdatedAt ?? 'N/A'}`} />
                </Stack>

                {error ? <Alert severity="warning">{error}</Alert> : null}

                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    <MetricTile icon={<StorageOutlinedIcon color="primary" />} label="Platform" value={platform} />
                    <MetricTile icon={<SpeedOutlinedIcon color="primary" />} label="CPU" value={cpuUsage} helper={cpuName} />
                    <MetricTile icon={<MemoryOutlinedIcon color="primary" />} label="Memory" value={memoryPercent} helper={`${memoryUsed} / ${memoryTotal}`} />
                </Stack>

                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    {gpus.length > 0 ? (
                        gpus.map((gpu, index) => {
                            const record = asRecord(gpu);
                            const name = asString(record.name ?? record.model, `GPU ${index + 1}`);
                            const usage = formatPercent(record.utilization_gpu ?? record.gpu_utilization ?? record.usage_percent ?? record.percent);
                            const memory = `${formatBytes(record.memory_used ?? record.memory_used_bytes)} / ${formatBytes(record.memory_total ?? record.memory_total_bytes)}`;
                            const temperature = asString(record.temperature_gpu ?? record.temperature, '');
                            const helper = temperature && temperature !== 'N/A' ? `${memory} - ${temperature}C` : memory;

                            return (
                                <MetricTile
                                    key={`${name}-${index}`}
                                    icon={<DeveloperBoardOutlinedIcon color="primary" />}
                                    label={name}
                                    value={usage}
                                    helper={helper}
                                />
                            );
                        })
                    ) : (
                        <MetricTile icon={<DeveloperBoardOutlinedIcon color="primary" />} label="GPU" value="N/A" helper="No GPU details returned" />
                    )}
                </Stack>
            </Stack>
        </Paper>
    );
}
